import React, {CSSProperties, useCallback, useEffect, useRef, useState} from 'react';
import {NavigationProps} from "../constants/Props";
import Button from "../components/Button";
import Screen from "../components/Screen";
import AdvanceTextInput from "../components/AdvanceTextInput";
import Image from "../components/Image";
import ButtonBar from "../components/ButtonBar";
import Icon from "../components/Icon";
import icons from "../css/Icon.module.css";
import Box from "../components/Box";
import {IOnboardingStore} from "../stores/OnboardingStore";
import imageStyles from '../css/Image.module.css'
import {IImage} from "../stores/models";
import {observer} from "mobx-react";
import Modals, {ModalsRefProps} from "../components/Modals";
import {ModalAction} from "../components/ModalDialog";
import {toI18N} from "../utils/I18n";
import {createInvalidImageFunction, createOpenModalFunction, usePrevious} from "../utils/HooksHelper";
import isUrl from "is-url";
import Alert, {AlertRefProps} from "../components/Alert";
import SkipButton from "../components/SkipButton";
import * as Analytics from "../utils/Analytics";

interface Props extends NavigationProps {
    title: string,
    subtitle: string,
    number: number,
    isSkippable?: boolean,
    setLandingPageBackgroundColor?: Function,
    setLandingPageTextColor?: Function
    setIsEditing?: (isEditing: (value: boolean) => boolean) => void,
    store: IOnboardingStore,
    isSharedPreview?: boolean,
    groupAvatar: IImage | null,
    isPreviewSlider?: boolean,
}

function OnboardingScreen(props: Props) {

    // Props

    let {
        number, onBack, groupAvatar,
        setIsEditing,
        onNext, isSkippable,
        isPreviewSlider,
        isSharedPreview,
        setLandingPageBackgroundColor,
        setLandingPageTextColor,
        store: {
            setTitle,
            setColor,
            setBackgroundColor,
            setLocalImage,
            setURLImage,
            removeBackgroundImage,
            setURLBackgroundImage,
            setLocalBackgroundImage,
            backgroundImage,
            setSubtitle,
            color,
            backgroundColor,
            image,
            removeImage,
            title,
            subtitle
        },
        isActive,
    } = props;

    const prevIsActive = usePrevious(isActive);
    useEffect(() => {
        if (!prevIsActive && isActive) {
            Analytics.logEvent(Analytics.events.land_on_intro_screen_page, {introScreenNumber: number});
        }
    });


    // State

    const [isPreview, setIsPreview] = useState(isPreviewSlider || false);

    // Ref

    const modalsRef = useRef<ModalsRefProps>(null);
    const alertRef = useRef<AlertRefProps>(null);

    // Callbacks

    const onPickGroupAvatarAsImage = useCallback(
        () => {
            if (!groupAvatar) {
                return;
            }
            if (!groupAvatar.isLocalImage) {
                setURLImage(groupAvatar.path);
                return
            }
            setLocalImage(groupAvatar.file)
        },
        [groupAvatar, setLocalImage, setURLImage],
    );

    const onClickColorPicker = useCallback(
        (e) => {
            modalsRef && modalsRef.current && modalsRef.current.showColorPickerModal(
                toI18N('color_picker_text_color_title'), v => {
                    setLandingPageTextColor && setLandingPageTextColor(v);
                    setColor(v)
                }, color
            );
        },
        [color, setLandingPageTextColor, setColor],
    );

    const onClickBackgroundColorPicker = useCallback(
        (e) => {
            modalsRef && modalsRef.current && modalsRef.current.showColorPickerModal(
                toI18N('color_picker_background_color_title'), (v) => {
                    setLandingPageBackgroundColor && setLandingPageBackgroundColor(v);
                    setBackgroundColor(v)
                }, backgroundColor
            );
        },
        [backgroundColor, setLandingPageBackgroundColor, setBackgroundColor],
    );

    const handleOnBackgroundError = useCallback(
        createInvalidImageFunction(alertRef, removeBackgroundImage),
        [removeBackgroundImage]
    );

    const handleOnError = useCallback(
        createInvalidImageFunction(alertRef, removeImage),
        [removeImage],
    );

    const toggleIsPreview = useCallback(() => {
        setIsEditing && setIsEditing(v => !v);
        !isSharedPreview && setIsPreview(v => !v);
    }, [setIsEditing, isSharedPreview]);

    const onOpenImageModal = useCallback(
        (e) => {

            const newOptions = new Array<ModalAction>(
                {
                    value: toI18N('image_picker_select_local_file'), isFileInput: true, onChange: setLocalImage
                },
                {
                    value: toI18N('image_picker_select_image_url'), keepModalShown: true, onClick: () => {
                        modalsRef && modalsRef.current && modalsRef.current.showTextInputModal(setURLImage);
                    }
                }
            );

            groupAvatar && (!image || groupAvatar.path !== image.path) && newOptions.push({
                value: toI18N('image_picker_select_group_avatar'),
                onClick: onPickGroupAvatarAsImage
            });

            image && newOptions.push({value: toI18N('image_picker_remove_image'), onClick: removeImage});

            newOptions.push({value: toI18N('general_cancel')});

            modalsRef && modalsRef.current && modalsRef.current.showActionsModal(newOptions);

        },
        [setURLImage, image, groupAvatar, removeImage, setLocalImage, onPickGroupAvatarAsImage],
    );

    const onOpenBackgroundImageModal = useCallback(
        createOpenModalFunction(modalsRef, setLocalBackgroundImage, setURLBackgroundImage, removeBackgroundImage, backgroundImage),
        [setURLBackgroundImage, backgroundImage, removeBackgroundImage, setLocalBackgroundImage],
    );

    const sameHeightStyle: CSSProperties = {height: 0, flex: 1};

    const renderButtonBar = () => {
        if (isPreviewSlider) {
            return (
                <ButtonBar right>
                    <Button primary onClick={onOpenBackgroundImageModal}>
                        <Icon white name={icons.iconPhoto}/>
                    </Button>
                    <Button primary onClick={onClickBackgroundColorPicker}>
                        <Icon white name={icons.iconBrush}/>
                    </Button>
                    <Button disabled={!props.store.canProceed} primary onClick={toggleIsPreview}>
                        <Icon white name={icons.iconTick}/>
                    </Button>
                </ButtonBar>
            )
        }
        return (
            <ButtonBar right>
                <Button primary onClick={toggleIsPreview}>
                    <Icon white name={icons.iconEye}/>
                </Button>
                <Button primary onClick={onOpenBackgroundImageModal}>
                    <Icon white name={icons.iconPhoto}/>
                </Button>
                <Button primary onClick={onClickBackgroundColorPicker}>
                    <Icon white name={icons.iconBrush}/>
                </Button>
                <Button disabled={!props.store.canProceed} primary onClick={props.onNext}>
                    <Icon white name={icons.iconArrowRight}/>
                </Button>
            </ButtonBar>
        )
    };

    return (
        <Screen
            title={props.title}
            subtitle={props.subtitle}
            onBackgroundError={handleOnBackgroundError}
            onBackgroundIsLoad={backgroundImage ? backgroundImage.setIsLoaded : undefined}
            backgroundImage={backgroundImage ? backgroundImage.path : undefined}
            backgroundColor={backgroundColor}
            hideLogo={isPreview}
            hideBack={isPreviewSlider || isPreview} onBack={onBack} hideNext>

            {isSkippable && !isPreview && <SkipButton onNext={onNext}/>}

            <Alert ref={alertRef}/>

            <Modals
                id={`onboarding-screen-${number}-${isPreviewSlider}`}
                ref={modalsRef}
                setIsPreview={setIsPreview}
                onCurtainClick={toggleIsPreview}
                modalTextInputValidation={isUrl}
                showCurtain={isPreview}/>

            <Box sideMargin center style={sameHeightStyle}>
                <AdvanceTextInput isPreview={isPreview}
                                  onChangeColorButtonPress={onClickColorPicker}
                                  maxLength={80}
                                  color={color}
                                  value={title}
                                  onChange={setTitle}
                                  id={`onboarding-screen-${number}-advance-text-1-${isPreviewSlider}`}
                                  placeholder={toI18N("onboarding_title_placeholder")}/>
            </Box>

            <Box center style={sameHeightStyle}>
                <Image onError={handleOnError} isPreview={isPreview} onClick={onOpenImageModal}
                       src={image ? image.path : undefined}
                       onLoad={image ? image.setIsLoaded : undefined}
                       className={image ? imageStyles.editableImageContainer : undefined} minHeight={180}
                       minWidth={240}/>
            </Box>

            <Box sideMargin center style={sameHeightStyle}>
                <AdvanceTextInput color={color}
                                  maxLength={150}
                                  onChangeColorButtonPress={onClickColorPicker}
                                  onChange={setSubtitle} xs
                                  isPreview={isPreview}
                                  value={subtitle}
                                  bottom
                                  id={`onboarding-screen-${number}-advance-text-2-${isPreviewSlider}`}
                                  placeholder={toI18N("onboarding_subtitle_placeholder")}/>
            </Box>
            {!isPreview && renderButtonBar()}

        </Screen>
    );
}

export default observer(OnboardingScreen);
